'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';

import { cn } from '@utils/cn';
import type { ExternalVideoStoryblok } from '@utils/storyblok/storyblok';

const ReactPlayer = dynamic(async () => await import('react-player'), {
  ssr: false,
});

type VideoProps = Pick<ExternalVideoStoryblok, 'thumbnail' | 'url'> &
  Partial<Pick<ExternalVideoStoryblok, 'autoplay' | 'fill' | 'loop' | 'startMuted'>> & {
    className?: string;
    controls?: boolean;
    isSquare?: boolean;
    showUtilityControls?: boolean;
  };

export function Video({
  autoplay = false,
  className,
  controls = true,
  fill = false,
  isSquare = false,
  loop = false,
  showUtilityControls = true,
  startMuted = true,
  thumbnail,
  url,
}: VideoProps) {
  const [playing, setPlaying] = useState(autoplay);
  const [isMuted, setIsMuted] = useState(startMuted);
  const [started, setStarted] = useState(() => autoplay || !thumbnail?.filename);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isFullscreenSupported, setIsFullscreenSupported] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsFullscreenSupported(
      Boolean(containerRef.current?.requestFullscreen && document.fullscreenEnabled),
    );

    const syncFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };

    document.addEventListener('fullscreenchange', syncFullscreenState);

    return () => document.removeEventListener('fullscreenchange', syncFullscreenState);
  }, []);

  const handleFullscreenToggle = async () => {
    if (!containerRef.current || !document.fullscreenEnabled) {
      return;
    }

    if (document.fullscreenElement === containerRef.current) {
      await document.exitFullscreen();
      return;
    }

    await containerRef.current.requestFullscreen();
  };

  const handlePlay = () => {
    setStarted(true);
    setPlaying(true);
  };

  return (
    <div
      className={cn(
        'group relative isolate w-full overflow-hidden  bg-black',
        fill ? 'h-full min-h-full' : 'aspect-video',
        isSquare && 'aspect-square',
        className,
      )}
      ref={containerRef}
    >
      {!started && thumbnail?.filename && (
        <button
          aria-label="Speel video af"
          className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 transition hover:bg-black/55"
          onClick={handlePlay}
          type="button"
        >
          <Image
            alt={thumbnail.alt || 'Video thumbnail'}
            className="object-cover"
            fill
            quality={75}
            sizes="(max-width: 768px) 100vw, 1200px"
            src={thumbnail.filename}
          />
          <span className="absolute inline-flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full border border-white/30 bg-white/15 text-white shadow-lg backdrop-blur-sm">
            <svg
              aria-hidden="true"
              className="ml-1 h-7 w-7"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5V19L19 12L8 5Z" />
            </svg>
          </span>
        </button>
      )}

      {started && (
        <ReactPlayer
          controls={controls}
          loop={loop}
          muted={isMuted}
          playing={playing}
          playsInline
          src={url}
          style={{ height: '100%', width: '100%' }}
          width="100%"
          height="100%"
        />
      )}

      {showUtilityControls && (
        <div className="absolute right-4 top-4 z-20 flex gap-2">
          {isFullscreenSupported && (
            <button
              className="rounded-full bg-black/60 px-3 py-1.5 text-sm text-white transition hover:bg-black"
              onClick={handleFullscreenToggle}
              type="button"
            >
              {isFullscreen ? 'Sluit scherm' : 'Vol scherm'}
            </button>
          )}
          <button
            className="rounded-full bg-black/60 px-3 py-1.5 text-sm text-white transition hover:bg-black"
            onClick={() => setIsMuted(!isMuted)}
            type="button"
          >
            {isMuted ? 'Geluid aan' : 'Geluid uit'}
          </button>
        </div>
      )}
    </div>
  );
}
